import React, { useEffect, useState, useMemo } from "react"
import Cookies from 'universal-cookie';
import ClipLoader from "react-spinners/ClipLoader";
import BeatLoader from "react-spinners/BeatLoader";
import { Space, Switch } from 'antd';
import { FetchAPI } from 'api/fetch';
import { vibrantUniqueColors } from 'utilities/CommonUtils';
import FullPageLoader from '../FullPageLoader/FullPageLoader'

const WmsFilters = ({ warehouseData, pageLayoutData, setWarehouseData, setSelectedEntity, binCounts, setBinCounts, selectedEntity, showAsideFilters, wmsContext }) => {
    const [formValues, setformValues] = useState(null);
    const [openForm, setOpenForm] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitializing, setIsInitializing] = useState(true);
    const [entityFilter, setEntityFilters] = useState({})
    const [entitySearchInput, setEntitySearchInput] = useState({})
    const cookies = new Cookies();

    const isStorageLocation = (name) => ["Bin", "Grid"].includes((name || "").trim());

    const dynamicParameters = {
        customer_id: wmsContext?.customer_id || cookies.get("customer_id"),
        user_id: wmsContext?.user_id || cookies.get("user_id"),
        userLocation_id: cookies.get("userLocation_id"),
        userRole_name: cookies.get("userRole_name"),
    }

    const formFieldsArray = useMemo(() => {
        if (formValues !== null) {
            return Object.entries(formValues).map(([key, value]) => ({
                id: key,
                ...value
            }));
        }
        return [];
    }, [formValues]);

        // Function to recursively collect all bins from warehouse data
        const collectAllBins = (data) => {
            if (!data || !data.children) return [];
            
            const allBins = [];
            
            const traverseEntity = (entity) => {
                if (!entity) return;
                
                // If this entity is a bin/grid, add it to the collection
                if (isStorageLocation(entity.en_name)) {
                    allBins.push(entity);
                }
                
                // If this entity has children, traverse them recursively
                if (entity.children && Array.isArray(entity.children) && entity.children.length > 0) {
                    entity.children.forEach(child => traverseEntity(child));
                }
            };
            
            // Start traversing from the root data's children
            data.children.forEach(child => traverseEntity(child));
            
            return allBins;
        };
        console.log("@binCounts",binCounts)

        const storageLocationLabel = useMemo(() => {
            const allBins = collectAllBins(warehouseData);
            const hasGrid = allBins.some(entity =>
                (entity.en_name || "").trim().toLowerCase() === "grid"
            );
            if (hasGrid) return "Grid";
            const hasBin = allBins.some(entity =>
                (entity.en_name || "").trim().toLowerCase() === "bin"
            );
            if (hasBin) return "Bin";
            return "Storage Location";
        }, [warehouseData]);
    
        // Function to filter bins based on selectedEntity
        const filterBinsBySelection = (allBins, selectedEntity) => {
            if (!selectedEntity.entity) {
                // No entity selected, return all bins
                return allBins;
            }
            
            // If an entity is selected, filter bins that belong to that entity
            const selectedEntityId = selectedEntity.entity.id;
            const selectedEntityType = selectedEntity.entity.en_name;
            
            return allBins.filter(bin => {
                // Find the parent hierarchy for each bin
                const findParentEntity = (entityId, entityType, searchData) => {
                    if (!searchData || !searchData.children) return null;
                    
                    const searchInChildren = (children) => {
                        for (let child of children) {
                            if (child.id === entityId && child.en_name === entityType) {
                                return child;
                            }
                            if (child.children) {
                                const found = searchInChildren(child.children);
                                if (found) return found;
                            }
                        }
                        return null;
                    };
                    
                    return searchInChildren(searchData.children);
                };
                
                // Check if bin belongs to the selected entity
                if (selectedEntityType === "Zone" || selectedEntityType === "Open Zone") {
                    // Check if bin is within the selected zone (direct or through rack/grid)
                    return bin.parent_entity_id === selectedEntityId || 
                           bin.zone_id === selectedEntityId ||
                           // Check if bin's parent rack/grid belongs to this zone
                           entityFilter.rack?.some(rack => 
                               rack.parent_entity_id === selectedEntityId && 
                               (bin.parent_entity_id === rack.id || bin.rack_id === rack.id)
                           );
                } else if (selectedEntityType === "Rack") {
                    // Check if bin belongs to the selected rack
                    return bin.parent_entity_id === selectedEntityId || 
                           bin.rack_id === selectedEntityId;
                } else if (isStorageLocation(selectedEntityType)) {
                    // If a specific bin/grid is selected
                    return bin.id === selectedEntityId;
                }
                
                return true; // Default: include all bins
            });
        };
    
        // Function to calculate bin counts
        const calculateBinCounts = (warehouseData, selectedEntity) => {
            if (!warehouseData) {
                setBinCounts({
                    totalBins: 0,
                    emptyBins: 0,
                    materialCount: 0
                });
                return;
            }
    
            // Collect all bins from the warehouse data
            const allBins = collectAllBins(warehouseData);
            
            // Filter bins based on selected entity (if any)
            const visibleBins = filterBinsBySelection(allBins, selectedEntity);
            console.log("@visibleBins",visibleBins)
            
            // Calculate the counts
            const totalBins = visibleBins.length;
            const emptyBins = visibleBins.filter(bin => 
                bin.is_bin_empty === true || 
                bin.is_bin_empty === 1 || 
                bin.isEmpty === true ||
                bin.is_bin_empty === "true" ||
                bin.is_bin_empty === "1"
            ).length;
            const materialCount = totalBins - emptyBins;
            
            // Log for debugging
            console.log('Bin Counts Calculation:', {
                totalBinsFound: allBins.length,
                visibleBinsAfterFilter: visibleBins.length,
                emptyBins,
                materialCount,
                selectedEntity: selectedEntity.entity?.en_name || 'None',
                selectedEntityName: selectedEntity.entity?.entity_name || 'None'
            });
            
            // Update the state
            setBinCounts({
                totalBins,
                emptyBins,
                materialCount
            });
        };
    
        // Calculate bin counts whenever warehouse data or selected entity changes
        useEffect(() => {
            // Update counts and repopulate entity filters (zones/racks) on data changes
            calculateBinCounts(warehouseData, selectedEntity);
            getEntitiesData();
        }, [warehouseData, selectedEntity.entity, setBinCounts]);


        useEffect(() => {
            if (pageLayoutData != 'undefined') {
                const allowedFields = JSON.parse(localStorage.getItem("wms"));
                const modifiedFormValues = pageLayoutData[0].formValues;
                for (let field in allowedFields) {
                    if (allowedFields[field] === false) {
                        delete modifiedFormValues[field];
                    }
                }
                setformValues(modifiedFormValues);
                setIsInitializing(false);
                fetchData(modifiedFormValues, pageLayoutData[0].filterInitEndpoint, allowedFields?.entity_id);
            }
        }, [pageLayoutData])

    const updateDropdown = (data, formFields) => {
        const updatedFields = JSON.parse(JSON.stringify(formFields));

        for (let key in updatedFields) {
            const field = updatedFields[key];
            const currentValue = formFields[key]?.value;
            const initialSelectValue = {
                displayValue: `Select ${field.label}`,
                value: null
            };

            if (field.elementType === "select") {
                field.elementConfig.options = data[key] ?? field.elementConfig.options ?? [];
                field.elementConfig.options = [
                    initialSelectValue,
                    ...field.elementConfig.options.filter(option => option.value !== null)
                ];
            }
        }

        return updatedFields;
    };

    const handleEntityChange = (event) => {
        const name = event.target.name;
        const value = event.target.value;
        const obj = {}
        obj[name] = value;
        setEntitySearchInput(prev => ({ ...prev, ...obj }))
        if (value === "") {
            getEntitiesData();
        }
    }

    const handleSubmitSearch = (e, type) => {
        const query = (entitySearchInput[type] || '').toString().toLowerCase();
        const submittedData = (entityFilter[type] || []).filter(entity =>
            (entity.entity_name || '').toString().toLowerCase() === query
        )
        if (submittedData?.length) {
            const obj = {}
            obj[type] = submittedData
            setEntityFilters(prevState => ({ ...prevState, ...obj }))
        }
        return;
    }

    const fetchData = async (formFields, endpoint, entity_id, type) => {
        if (pageLayoutData != "undefined") {
            let getURL = endpoint;
            const payload = {
                ...dynamicParameters,
                entity_id: entity_id ?? null,
                entity_type: type,
                start_from: 0
            };
            let paramsConverted = new URLSearchParams(payload);
            getURL = (`${getURL}?${paramsConverted.toString()}`);
            const response = await FetchAPI(getURL);
            const responseJson = await response.json();
            const fieldValues = updateDropdown(responseJson.data, formFields);
            setformValues(fieldValues);
        }
    }

    const handleInputChange = (e) => {
        const value = e.target.value;
        const name = e.target.name
        const formFields = JSON.parse(JSON.stringify(formValues));
        formFields[name].value = value
        if (formFields[name].update.required) {
            fetchData(formFields, formFields[name].update.endPoint, value, name);
        } else {
            setformValues(formFields);
        }
    }

    // Build and append data-driven entities to the fetched floor
    // Replaced legacy PalletsArea (Pallet + CardboardBox tree) with OpenZone Bin grid + palletConfig
    const buildPalletHierarchy = (floor) => {
      if (!floor) return floor;
      const augmented = JSON.parse(JSON.stringify(floor));
      augmented.children = Array.isArray(augmented.children) ? augmented.children : [];

      // Prefer to place pallets inside an OpenZone/Openzone, else first Zone, else attach at root
      const openZone = augmented.children.find(c => c && c.en_name === 'OpenZone');
        // --- New: Also synthesize an OpenZone Bin grid (2 x 6) with palletConfig so Bin -> Pallet is data-driven ---
        // Attach palletConfig to existing OpenZone Grid entities, or synthesize a Bin grid for legacy APIs
        try {
            const targetZone = openZone;
            if (targetZone) {
                targetZone.children = Array.isArray(targetZone.children) ? targetZone.children : [];

                const palletConfig = {
                    palletVariant: 'full',
                    palletsAlongX: 2,
                    palletsAlongZ: 2,
                    palletsGapX: 0.9,
                    palletsGapZ: 2.8,
                    squarePallet: true,
                    tiersAlongY: 2,
                    tiersGapY: 0,
                    // Additional overrides so Bin.jsx is fully data-driven
                    palletHeightOverride: 0.6,
                    innerPadOverride: 0.23,
                    fitRatioOverride: 1.01,
                    boxesConfig: { cols: 2, rows: 2, layers: 1, gap: 0.4 },
                    boxInsetX: 1.0,
                    boxInsetZ: 1.0
                };

                const hasGrid = targetZone.children.some(c => c && c.en_name === 'Grid');

                if (hasGrid) {
                    targetZone.children.forEach(c => {
                        if (c && isStorageLocation(c.en_name)) {
                            c.palletConfig = palletConfig;
                            if (typeof c.is_bin_empty === 'undefined') c.is_bin_empty = false;
                            if (typeof c.zone_id === 'undefined') c.zone_id = targetZone.id;
                            if (typeof c.parent_entity_id === 'undefined') c.parent_entity_id = targetZone.id;
                        }
                    });
                } else {
                    const zoneX = targetZone.xco || 0;
                    const zoneZ = targetZone.zco || 0;
                    const zoneY = targetZone.yco || 0;
                    const zoneW = Math.max(40, targetZone.width || 120);
                    const zoneL = Math.max(40, targetZone.length || 120);

                    // Layout: 2 rows (X) x 6 columns (Z)
                    const rows = 2;
                    const cols = 6;
                    const gapX = 6;   // aisle gap between bins along X
                    const gapZ = 8;   // aisle gap between bins along Z

                    // Compute bin dimensions from zone footprint
                    const totalGapX = (rows - 1) * gapX;
                    const totalGapZ = (cols - 1) * gapZ;
                    const binW = Math.max(16, (zoneW - totalGapX) / rows);
                    // Apply a reduction along Z-axis (length) to make OpenZone bins shallower
                    const lengthScale = 0.85; // 85% of previous size
                    const binL = Math.max(16, ((zoneL - totalGapZ) / cols) * lengthScale);
                    const binH = 10; // nominal height

                    // Start (top-left) so grid is centered in zone
                    const startX = zoneX - (rows * binW + totalGapX) / 2 + binW / 2;
                    const startZ = zoneZ - (cols * binL + totalGapZ) / 2 + binL / 2;

                    // Start bin IDs from the next numeric id after existing children to avoid collisions
                    const existingIds = (targetZone.children || [])
                        .map(c => c && c.id)
                        .filter(id => typeof id === 'number' || (typeof id === 'string' && /^\d+$/.test(id)))
                        .map(id => Number(id));
                    let idCounter = (existingIds.length ? Math.max(...existingIds) : 0) + 1;
                    for (let r = 0; r < rows; r++) {
                        for (let c = 0; c < cols; c++) {
                            const bx = startX + r * (binW + gapX);
                            const bz = startZ + c * (binL + gapZ);
                            const bin = {
                                id: idCounter++,
                                en_name: 'Bin',
                                entity_name: `OZ-Bin-${r + 1}-${c + 1}`,
                                xco: bx,
                                yco: zoneY,
                                zco: bz,
                                width: binW,
                                length: binL,
                                height: binH,
                                // Link to parent Zone so selection filters and counters include these bins
                                parent_entity_id: targetZone.id,
                                zone_id: targetZone.id,
                                is_bin_empty: false,
                                // Data-driven pallet layout for Bin.jsx to consume
                                palletConfig
                            };
                            targetZone.children.push(bin);
                        }
                    }
                }
            }
        } catch (e) {
            console.warn('[buildPalletHierarchy] Failed to synthesize OpenZone Bin grid', e);
        }

        return augmented;
    };

    const handleSubmitForm = async () => {
        let submitData = {}
        for (let key in formValues) {
            submitData[key] = formValues[key].value
        }
        let getURL = pageLayoutData[0].filterEndPoint;
        const payload = {
            ...submitData,
            ...dynamicParameters,
            entity_id: submitData.floor ?? null,
            start_from: 0
        };
        setIsLoading(true);
        const paramsConverted = new URLSearchParams(payload);
        getURL = (`${getURL}?${paramsConverted.toString()}`);
        const response = await FetchAPI(getURL);
        const responseJson = await response.json();
        setIsLoading(false);
        // Augment the fetched floor with a static Zone5 (OpenZone) regardless of API response
        const floor = responseJson?.data?.data?.[0];
        if (floor) {

    setWarehouseData(buildPalletHierarchy(floor));
} else {
    // Append Pallets > CardboardBoxes hierarchy before storing
    setWarehouseData(buildPalletHierarchy(floor));
}

}

const handleSelectedEntity = (entity, color) => {
    // Compute the next selection synchronously
    console.log("@34", entity)
    let nextSelected;
    setSelectedEntity(prevState => {
        if (prevState.entity === null) {
            nextSelected = { ...prevState, entity };
            return nextSelected;
        } else if (prevState.entity.id === entity.id) {
            nextSelected = { ...prevState, entity: null };
            return nextSelected;
        } else {
            nextSelected = { ...prevState, entity };
            return nextSelected;
        }
    });
    // Recalculate counts immediately for responsive UI (especially for OpenZone)
    const effectiveSelected = nextSelected ?? { entity };
    calculateBinCounts(warehouseData, effectiveSelected);
}

const renderFormFilters = () => {
    return (
        <div className="header-bar">
            {(openForm) && (
                <>
                    <div className="filters">
                        <div className="filters-form">
                            {formFieldsArray?.length &&
                                formFieldsArray.map((field) => {
                                    if (field.elementType === 'select') {
                                        return (
                                            <select
                                                onChange={handleInputChange}
                                                className="form-select"
                                                name={field.id}
                                                value={field.value}
                                                key={field.id}
                                            >
                                                {field.elementConfig.options.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.displayValue}
                                                    </option>
                                                ))}
                                            </select>
                                        );
                                    } else {
                                        return (
                                            <input
                                                onChange={handleInputChange}
                                                className="form-input"
                                                name={field.id}
                                                type="text"
                                                placeholder={field.elementConfig.placeholder}
                                                value={field.value}
                                                key={field.id}
                                            />
                                        );
                                    }
                                })}
                        </div>
                        <div className='button-switch-container'>
                            {warehouseData && (
                                <div className="view-toggle">
                                    <Space direction="vertical">
                                        <Switch
                                            checkedChildren="3D"
                                            unCheckedChildren="2D"
                                            defaultChecked
                                            // onChange={(e) => setIs3DView(e)}
                                        />
                                    </Space>
                                </div>
                            )}
                            {pageLayoutData != "undefined" && <button onClick={handleSubmitForm} style={{ width: '7rem', height: "3rem" }} className="submit-button">
                                {isLoading ? <ClipLoader size={13} color="white" /> : "Submit"}
                            </button>}
                        </div>
                    </div>
                </>)
            }
            {window.innerWidth <= 768 && <div className="line-toggle-container">
                <hr className="line" />
                <div className="icon" onClick={() => setOpenForm(!openForm)} >
                    {openForm ? "Hide" : "Filters"}
                    <i class={`fa fa-caret-${openForm ? "up" : "down"}`} aria-hidden="true" />
                </div>
                <hr className="line" />
            </div>}
        </div>
    )
}

const renderFilterNavbar = () => {
    return (
        <div
            className="wms-filter-navbar"
            style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '1rem',
                height: '3rem',
                ...(showAsideFilters ? { width: '75%' } : { width: '100%' })
            }}
        >
            <div className="no-bins">
                <div className="no-bin-text">Number of {storageLocationLabel}s:</div>
                <div className="no-bin-value">{binCounts.totalBins === 0 ? <BeatLoader size={10} /> : binCounts.totalBins}</div>
            </div>
            {/* <div className="no-bins">
                <div className="no-bin-text">Material Count</div>
                <div className="no-bin-value">{binCounts.materialCount}</div>
            </div> */}
            <div className="no-bins">
                <div className="no-bin-text">Empty {storageLocationLabel}s:</div>
                <div className="no-bin-value">{binCounts.emptyBins}</div>
            </div>
            {/* <div className="bin-category">
                <div className="bin-category-text">Categories</div>
                <div className="bin-category-button">
                    <button>Fast</button>
                    <button>Medium</button>
                    <button>Slow</button>
                </div>
            </div> */}
        </div>
    )
}

const renderEntities = (entities) => {
    return Object.entries(entities).map(([type, list]) => {
        const rawName = list[0]?.en_name || type;
        const entityName = rawName
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');
        return (
        <div key={type} className="entity-sub-container">
            <div className="entity-name">{`${entityName}s`.toUpperCase()}</div>
            <div className="wms-filter-item">
                <div className="entity-search-container" >
                    <input type="text" name={type} className="entity-search" placeholder={`Search ${entityName}s`} onChange={handleEntityChange} value={entitySearchInput[type] || ''} />
                    <button className="submit-button" onClick={(e) => handleSubmitSearch(e, type)} >Search</button>
                </div>
                {list.map((entity, index) => (
                    <button
                        className="wms-filter-button"
                        style={(selectedEntity?.entity?.id === entity.id) ? { background: entity.color, color: "white" } : {}}
                        onClick={() => handleSelectedEntity(entity, vibrantUniqueColors)}
                        key={entity.id}
                    >{entity.entity_name}
                    </button>
                ))}
            </div>
        </div>
        )
    });
};



const getEntitiesData = () => {
    if (!warehouseData || !warehouseData.children) return;

    const entitiesMap = {};

    const extractEntities = (entity, color) => {
        if (!entity || typeof entity !== 'object') return;

        const type = (entity.en_name || '').toString().trim().toLowerCase();
        if (!type || type === 'floor') return;

        entity.color = color;
        if (!entitiesMap[type]) entitiesMap[type] = [];
        entitiesMap[type].push(entity);

        if (Array.isArray(entity.children) && entity.children.length > 0) {
            entity.children.forEach(child => extractEntities(child, color));
        }
    };

    warehouseData.children.forEach((child, index) => {
        const color = vibrantUniqueColors[index % vibrantUniqueColors.length];
        extractEntities(child, color);
    });

    setEntityFilters(entitiesMap);
}


    return (
        <div className="filters-main-3d">
            {isInitializing ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                    <FullPageLoader />
                </div>
            ) : (
                <>
                    {formFieldsArray?.length > 0 && renderFormFilters()}
                    {warehouseData && (
                        <>
                            {renderFilterNavbar()}
                            {showAsideFilters && (
                                <div className="wms-filter-container">
                                    {renderEntities(entityFilter)}
                                </div>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    )
}
export default WmsFilters